import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PushCard } from "@/components/push/PushCard";
import { SignedInDevices } from "@/components/auth/SignedInDevices";
import { toast } from "sonner";
import { LogOut, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FormError, PageHeader } from "@/components/common/ui-bits";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function ProfileForm({ user, setUser }) {
  const [form, setForm] = useState({ name: user.name, email: user.email });
  const [error, setError] = useState("");
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/auth/me", form);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="surface p-5 sm:p-6 mb-6 space-y-4 fade-up" noValidate data-testid="profile-form">
      <h2 className="font-heading text-lg font-semibold">Name & email</h2>
      <div><Label htmlFor="p-name" className="text-sm">Name</Label>
        <Input id="p-name" value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-xl" data-testid="profile-name-input" /></div>
      <div><Label htmlFor="p-email" className="text-sm">Email (used to sign in and for alert emails)</Label>
        <Input id="p-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5 h-11 rounded-xl" data-testid="profile-email-input" /></div>
      <FormError message={error} testId="profile-error" />
      <Button type="submit" className="rounded-xl h-11" data-testid="profile-save-button">Save</Button>
    </form>
  );
}

export default function AccountPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };

  const toggleAlerts = async (on) => {
    try {
      const { data } = await api.patch("/auth/me/preferences", { email_alerts: on });
      setUser(data);
      toast.success(on ? "Budget alert emails turned on" : "Budget alert emails turned off");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) return setError("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  return (
    <div className="max-w-xl mx-auto">
      <PageHeader eyebrow="Your profile" title="Account" testId="account-title" />
      <div className="surface p-5 sm:p-6 mb-6 fade-up" data-testid="account-profile">
        <div className="font-heading text-lg font-semibold" data-testid="account-name">{user.name}</div>
        <div className="text-sm text-muted-foreground" data-testid="account-email">{user.email}</div>
        <div className="mt-3 inline-flex rounded-full bg-accent text-accent-foreground px-3 py-1 text-xs font-semibold" data-testid="account-role">
          {user.role === "admin" ? "Administrator" : "Standard user"}
        </div>
      </div>

      <Link to="/install" className="surface mb-6 px-5 py-4 flex items-center gap-3 font-semibold text-sm hover:bg-secondary/40 fade-up" data-testid="account-install-link"><Smartphone className="h-5 w-5 text-sky-700" />Install the app</Link>
      <SignedInDevices />
      {user.role === "admin" && <PushCard />}
      {user.role === "admin" && <ProfileForm user={user} setUser={setUser} />}

      {user.role === "admin" && (
        <div className="surface p-5 sm:p-6 mb-6 flex items-center justify-between gap-4 fade-up" data-testid="email-alerts-setting">
          <div>
            <Label htmlFor="email-alerts" className="font-heading text-base font-semibold">Email me budget alerts</Label>
            <p className="text-sm text-muted-foreground mt-0.5">Get an email when a budget reaches 80% (approaching) or 100% (over) in a month, and bill due-date reminders.</p>
          </div>
          <Switch id="email-alerts" checked={user.email_alerts !== false} onCheckedChange={toggleAlerts} data-testid="email-alerts-toggle" />
        </div>
      )}

      <form onSubmit={submit} className="surface p-5 sm:p-6 space-y-4 fade-up" noValidate data-testid="change-password-form">
        <h2 className="font-heading text-lg font-semibold">Change password</h2>
        {[["current", "Current password", "current-password"], ["next", "New password (min 8 characters)", "new-password"], ["confirm", "Confirm new password", "new-password"]].map(([k, label, ac]) => (
          <div key={k}>
            <Label htmlFor={`pw-${k}`} className="text-sm">{label}</Label>
            <Input id={`pw-${k}`} type="password" autoComplete={ac} value={form[k]} onChange={set(k)} className="mt-1.5 h-11 rounded-xl" data-testid={`change-password-${k}-input`} />
          </div>
        ))}
        <FormError message={error} testId="change-password-error" />
        <Button type="submit" disabled={busy} className="rounded-xl h-11" data-testid="change-password-submit-button">Update password</Button>
      </form>

      <Button variant="outline" onClick={doLogout} className="mt-6 w-full h-12 rounded-xl text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800" data-testid="account-logout-button">
        <LogOut className="h-4 w-4 mr-2" /> Log out
      </Button>
    </div>
  );
}
